import { createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { axios } from "config/api";
import {
  Auth,
  confirmPasswordReset,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as signOutFirebase,
  updatePassword as firebaseUpdatePassword,
  User,
  verifyPasswordResetCode,
} from "firebase/auth";
import {
  ConfirmResetData,
  ResetData,
  SignInData,
  UpdatePasswordData,
} from "interfaces/requests/Auth";
import { SignInResponse } from "interfaces/responses/Auth";
import {
  FirebaseAuthErrorCode,
  firebaseErrorMessages,
} from "libraries/firebase/errorMessages";
import auth from "libraries/firebase/firebase";

const signInAndGetUserWithToken = async (
  auth: Auth,
  email: string,
  password: string,
): Promise<{ user: User; token: string }> => {
  const response = await signInWithEmailAndPassword(auth, email, password);

  const user = response.user;

  const token = await user.getIdToken();

  return {
    user,
    token,
  };
};

const updateHasLoggedInClaim = async (token: string, hasLoggedIn: boolean) => {
  const response = await axios.put(
    "/auth/update-claim",
    { hasLoggedIn },
    {
      headers: {
        authorization: "Bearer " + token,
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to update claim.");
  }
};

export const signInWithCredentials = createAsyncThunk(
  "auth/signInWithCredentials",
  async (
    data: SignInData,
    { rejectWithValue },
  ): Promise<SignInResponse | unknown> => {
    try {
      const { user, token } = await signInAndGetUserWithToken(
        auth,
        data.email,
        data.password,
      );

      const {
        claims: { hasLoggedIn },
      } = await user.getIdTokenResult();

      if (hasLoggedIn === true || hasLoggedIn === undefined) {
        localStorage.setItem("token", token);
        return true;
      }

      return false;
    } catch (error: any) {
      const errorCode = error?.code as FirebaseAuthErrorCode;

      const errorMessage =
        firebaseErrorMessages[errorCode] || "Unexpected error.";

      return rejectWithValue({ code: errorCode, message: errorMessage });
    }
  },
);

export const updatePassword = createAsyncThunk(
  "auth/updatePassword",
  async (
    data: UpdatePasswordData,
    { rejectWithValue },
  ): Promise<void | unknown> => {
    const auth = getAuth();

    try {
      const user = auth.currentUser;

      if (!user) {
        throw new Error("No user is authenticated.");
      }

      const token = await user.getIdToken(true);

      localStorage.setItem("token", token);

      await updateHasLoggedInClaim(token, true);

      await firebaseUpdatePassword(user, data.newPassword);
    } catch (error: any) {
      const token = localStorage.getItem("token");

      if (token) await updateHasLoggedInClaim(token, false);

      return rejectWithValue(error.message || "Authentication failed.");
    }
  },
);

export const sendResetPasswordEmail = createAsyncThunk(
  "auth/sendResetPasswordEmail",
  async (data: ResetData, { rejectWithValue }): Promise<void | unknown> => {
    const auth = getAuth();
    const { email } = data;
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      console.error("Error resetting password:", error.message);
      return rejectWithValue(error.message || "Authentication failed.");
    }
  },
);

export const confirmResetPassword = createAsyncThunk(
  "auth/confirmResetPassword",
  async (
    data: ConfirmResetData,
    { rejectWithValue },
  ): Promise<void | unknown> => {
    const auth = getAuth();
    const { newPassword, code } = data;

    try {
      const verifiedEmail = await verifyPasswordResetCode(auth, code);

      if (!verifiedEmail) throw new Error("Password reset code is invalid");

      await confirmPasswordReset(auth, code, newPassword);

      const { token } = await signInAndGetUserWithToken(
        auth,
        verifiedEmail,
        newPassword,
      );

      localStorage.setItem("token", token);

      await updateHasLoggedInClaim(token, true);
    } catch (error: any) {
      const token = localStorage.getItem("token");

      if (token) await updateHasLoggedInClaim(token, false);

      console.error("Error confirming reset password:", error.message);
      return rejectWithValue(error.message || "Authentication failed.");
    }
  },
);

export const getMe = createAsyncThunk(
  "auth/getMe",
  async (_, { rejectWithValue }): Promise<void | unknown> => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No token found");
      }

      const response = await axios.get("/user/profile", {
        headers: {
          authorization: "Bearer " + token,
        },
      });

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to get user.",
      );
    }
  },
);

export const signOut = createAsyncThunk(
  "auth/signOut",
  async (_, { rejectWithValue, dispatch }): Promise<void | unknown> => {
    try {
      await signOutFirebase(auth);

      localStorage.removeItem("token");
      dispatch(resetStore());
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
